import { ChallengeService } from '@duck/api-definitions'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { DataWorkspace } from '@/components/DataWorkspace'
import { useAtomValue, useSetAtom } from 'jotai'
import { WorkspaceHeaderPane } from './WorkspaceHeaderPane'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import { useTar } from '@/modules/i18n'
import { MarkdownEditor, Button } from '@duck/ui'
import { useState, useEffect } from 'react'

export interface MobileWorkspaceProps {
    challenge: ChallengeService.ChallengeInfo['challenge']
    task: ChallengeService.ChallengeInfo['task']
    charts: ChallengeService.WorkState['charts']
    challengeId: string
    workTime: number
    handleSolutionChange: (solution: string) => void
    handleChartAdded: (solution: string, chart: ChallengeService.Chart) => void
    handleChartRemoved: (solution: string, remainingChartIds: string[]) => void
}

export const MobileWorkspace = ({
    challenge,
    task,
    charts,
    challengeId,
    workTime,
    handleSolutionChange,
    handleChartAdded,
    handleChartRemoved,
}: MobileWorkspaceProps) => {
    const currentSolution = useAtomValue(currentSolutionAtom)
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const { t } = useTar()
    const [activeTab, setActiveTab] = useState<'assignment' | 'query' | 'solution'>('assignment')

    // Load solution from challenge on mount and when challenge changes
    useEffect(() => {
        const solutionFromChallenge = challenge.workState?.solution || ''
        setCurrentSolution(solutionFromChallenge)
    }, [challenge, setCurrentSolution])

    return (
        <div className="flex flex-col flex-1 relative">
            <div className="p-4">
                <WorkspaceHeaderPane
                    challenge={challenge}
                    task={task}
                    workTime={workTime}
                />
            </div>
            <div className="flex gap-2 p-4 border-b border-border w-full">
                <Button
                    onClick={() => setActiveTab('assignment')}
                    variant={activeTab === 'assignment' ? 'default' : 'outline'}
                    className="basis-1/3"
                >
                    {t('common.assignment')}
                </Button>
                <Button
                    onClick={() => setActiveTab('query')}
                    variant={activeTab === 'query' ? 'default' : 'outline'}
                    className="basis-1/3"
                >
                    {t('common.data')}
                </Button>
                <Button
                    onClick={() => setActiveTab('solution')}
                    variant={activeTab === 'solution' ? 'default' : 'outline'}
                    className="basis-1/3"
                >
                    {t('common.solution')}
                </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
                {activeTab === 'assignment' && (
                    <div className="prose prose-slate dark:prose-invert max-w-none overflow-y-auto custom-scrollbar">
                        <MarkdownEditor
                            defaultValue={task.body}
                            mdClassName="max-w-none border-none"
                            mode="view"
                        />
                    </div>
                )}
                {activeTab === 'query' && challengeId && (
                    <DataWorkspace
                        challengeId={challengeId}
                        tableQueries={task.tableQueries}
                    />
                )}
                {activeTab === 'solution' && charts && (
                    <div className="max-w-[100dvw]">
                        <WysiwygEditor
                            content={currentSolution}
                            onChange={(solution) => {
                                handleSolutionChange(solution)
                                setCurrentSolution(solution)
                            }}
                            handleChartAdded={handleChartAdded}
                            handleChartRemoved={handleChartRemoved}
                            startingCharts={charts}
                            placeholder={t('common.solveHere')}
                            className=''
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
